/**
 * IVT Hub authorization, as the frontend sees it: which LDAP groups admit a user to
 * the /ivt/* surface, and how to turn a 403 into advice the user can act on.
 *
 * Two axes exist server-side and they are independent:
 *   - ROLE  — an LDAP group. `ivthub` is view-only; `ivthub-editor` / `ivthub-admin`
 *             additionally carry the write role. Every operation that is not
 *             @readonly (except CreateGrant / RevokeGrant) requires the write role.
 *             The admin surface (`/admin/*`) is the one tier where the editor role is
 *             NOT sufficient, and where a READ is gated as well.
 *   - SCOPE — `ivt_permission_rule`, i.e. which industries' data you may touch.
 * A caller can hold the role and still be denied by scope, and the two have opposite
 * remedies, which is why `denialKindOf` distinguishes them rather than printing one
 * "no permission" string.
 *
 * The frontend gate is UX only — the authoritative check is the service's Layer-2
 * gate. This module exists so the two do not disagree: before it, the frontend
 * required exactly `ivthub`, which would have locked out an editor-only or
 * admin-only user the backend already admits.
 */

/** View-only membership. Held by every IVT Hub user. */
export const IVT_VIEWER_GROUP = 'ivthub';
/** Write role. Membership follows the IVT reporting line. */
export const IVT_EDITOR_GROUP = 'ivthub-editor';
/** Superset of the editor role (full rights), maintained as a manual list. */
export const IVT_ADMIN_GROUP = 'ivthub-admin';

/**
 * Any one of these admits a user to /ivt/*. Mirrors the backend's admission set
 * (`IvtHubGroups`: the union of every route tier's accepted groups) — keep them in
 * step, or the frontend denies users the service would serve and vice versa.
 */
export const IVT_ACCESS_GROUPS: readonly string[] = [
  IVT_VIEWER_GROUP,
  IVT_EDITOR_GROUP,
  IVT_ADMIN_GROUP,
];

/**
 * True when the user may open the IVT surface at all. Deliberately "any of", not
 * "has `ivthub`": the three groups are separately assignable, and editor/admin are
 * not guaranteed to also carry `ivthub` (they happen to today).
 */
export function hasIvtAccess(ldapGroups: readonly string[] | undefined): boolean {
  return (ldapGroups ?? []).some((g) => IVT_ACCESS_GROUPS.includes(g));
}

/**
 * True when the user may open the admin surface (`/ivt/admin/*`).
 *
 * Narrower than {@link hasIvtAccess} on purpose, and the only place the frontend
 * treats the three groups as anything other than interchangeable: the admin routes
 * are the one tier where holding the editor role is not enough
 * (`IvtHubAuthorizer.Requirement.ADMIN_ONLY`), and their GET is gated too, so there
 * is no "can read, cannot write" state to render.
 *
 * Still UX only — the service decides. This exists so an editor is not shown a nav
 * item that will 403.
 */
export function hasIvtAdminAccess(ldapGroups: readonly string[] | undefined): boolean {
  return (ldapGroups ?? []).includes(IVT_ADMIN_GROUP);
}

/**
 * True when the user may write (`IvtHubAuthorizer.Requirement.EDITOR_ONLY`). Admin is a superset
 * of the editor role, hence the `||`: an admin who does not also carry `ivthub-editor` may still
 * write. UX only, like the rest of this module — a caller must still handle a denied write.
 */
export function hasIvtEditorAccess(ldapGroups: readonly string[] | undefined): boolean {
  return hasIvtAdminAccess(ldapGroups) || (ldapGroups ?? []).includes(IVT_EDITOR_GROUP);
}

/**
 * Why a 403 happened. `null` (from `denialKindOf`) means the failure was not an
 * authorization denial at all, so the caller should fall back to its own message.
 */
export type DenialKind =
  /** Member, but lacks the write role, on an operation that modifies data. */
  | 'write-not-permitted'
  /** Member — possibly even an editor — on an admin-surface route, lacking `ivthub-admin`. */
  | 'admin-not-permitted'
  /** The route is not registered for authorization — a service config bug, not the user's problem. */
  | 'route-unclassified'
  /** Holds the role, but not for this industry (industry scoping). */
  | 'industry-not-permitted'
  /** Not an IVT Hub member, or authentication failed outright. */
  | 'not-authorized';

/**
 * Classifies a caught error against the service's 403 contract.
 *
 * The contract has three shapes, and `reason` — NOT the exception name — is the
 * discriminator the service guarantees:
 *
 * | what happened            | x-amzn-ErrorType     | body                                    |
 * |--------------------------|----------------------|-----------------------------------------|
 * | lacks the write role     | `ForbiddenException` | `{message, reason: "write-not-permitted"}` |
 * | lacks the admin role     | `ForbiddenException` | `{message, reason: "admin-not-permitted"}` |
 * | route not registered     | `ForbiddenException` | `{message, reason: "route-unclassified"}`  |
 * | not a member / authn bad | (absent)             | `{message}`                              |
 *
 * `reason` is intentionally NOT in the Smithy model, and reading it needs no
 * regenerated client: rest-json's error path hands the whole parsed body to
 * `decorateServiceException`, which copies members the exception shape does not
 * declare straight onto the instance. (Verified against the generated client — this
 * holds for the modelled path AND the fallback path below. It does NOT generalise to
 * success responses, where undeclared members are dropped — the deserializer walks the
 * runtime schema, so `SolutionMapEntry$`'s member list in
 * `@amzn/ivthub-client/dist-es/schemas/schemas_0.js` is what decides that. There used to be
 * a raw-fetch workaround in IvtHubClient.ts for exactly this; it is gone because the field
 * it recovered is declared now.)
 *
 * Why the checks are in this order:
 *   1. `reason` first, because it is the only signal present on BOTH 403 paths.
 *   2. `name === 'ForbiddenException'` with no `reason` means the exception was
 *      thrown by the service's own activity code rather than the gate — the only
 *      such throw is CreateMotion's industry denial (every other industry denial is
 *      a deliberate 404, so "exists but forbidden" is indistinguishable from
 *      "missing"). Hence the industry reading is safe.
 *   3. Status 403 with neither: the gate short-circuits BEFORE Coral and omits
 *      x-amzn-ErrorType, so the client cannot resolve an error shape and throws
 *      `IVTHubServiceException` with `name === 'Unknown'`. Matching on the name
 *      would miss this case entirely — the status is the only signal left.
 */
export function denialKindOf(e: unknown): DenialKind | null {
  const reason = (e as { reason?: string } | null)?.reason;
  if (reason === 'write-not-permitted') return 'write-not-permitted';
  // Must be matched BEFORE the ForbiddenException fallback below. Without this branch
  // an admin denial has a `reason` this function does not know, falls through to
  // step 2, and is reported as an industry-scope problem — telling the user to
  // request industry data access when what they need is the ivthub-admin group.
  // That misdirection is exactly what the service added this reason to prevent.
  if (reason === 'admin-not-permitted') return 'admin-not-permitted';
  if (reason === 'route-unclassified') return 'route-unclassified';
  if ((e as { name?: string } | null)?.name === 'ForbiddenException') {
    return 'industry-not-permitted';
  }
  const status = (e as { $metadata?: { httpStatusCode?: number } } | null)?.$metadata
    ?.httpStatusCode;
  return status === 403 ? 'not-authorized' : null;
}

/**
 * Copy per denial kind. Two rules hold across all four:
 *   - No "请重试". A 403 is not retryable; telling the user to retry sends them into
 *     a loop and reads as a system fault. (Same judgement as the non-retryable 404 /
 *     409 branches in AssetDetailHeader.)
 *   - Say which group or scope to ask for. Affected users are not notified in
 *     advance, so this string is the only place "go ask for what" is written down.
 */
export const DENIAL_HINTS: Record<DenialKind, string> = {
  'write-not-permitted': `此操作会修改数据，需要 ${IVT_EDITOR_GROUP} LDAP 组权限；当前账号只有查看权限。请联系 IVT Hub 团队申请加入 ${IVT_EDITOR_GROUP} 组。`,
  // Names the group to request and nothing else. It used to add "ivthub-editor 在这里不够用",
  // which the comment here already described as absent — the sentence had drifted in against
  // the stated intent. Removed 2026-09-18 on the product owner's call: a caller who is not an
  // admin does not need to be told which OTHER group also fails, and enumerating the group
  // hierarchy in an error is more internal detail than the reader can use.
  // Still does NOT claim the operation modifies data: the admin surface gates its GET too, so
  // the write-oriented wording would be false for it.
  'admin-not-permitted': `该操作仅限 IVT Hub 管理员，需要 ${IVT_ADMIN_GROUP} LDAP 组权限。请联系 IVT Hub 团队申请加入 ${IVT_ADMIN_GROUP} 组。`,
  // Deliberately does NOT tell the user to request a group: joining one cannot fix an
  // unregistered route. This string is currently the only detection path for the bug
  // (no metric or alarm exists yet), so it asks the user to report it.
  'route-unclassified':
    '服务端没有为该操作登记授权规则 —— 这是服务配置问题，不是权限不足，申请 LDAP 组也不会生效。请联系 IVT Hub 团队处理。',
  'industry-not-permitted': '没有该行业的操作权限。请联系 IVT Hub 团队申请该行业的数据权限。',
  // The ONLY kind where "刷新重新登录" is correct advice — an expired or malformed
  // id_token lands here. Keeping it out of the other three is the point of this
  // module: the previous single message offered re-login for a missing group, which
  // made people log in repeatedly and conclude the system was broken.
  'not-authorized': `IVT Hub 身份校验未通过：当前账号可能不在 ${IVT_VIEWER_GROUP} LDAP 组中，或登录已过期。请刷新页面重新登录；若仍失败，请联系 IVT Hub 团队申请 IVT Hub 访问权限。`,
};

/**
 * The same messages in English, for surfaces whose own copy is English.
 *
 * A second table rather than translating {@link DENIAL_HINTS}: every other IVT page
 * writes its own errors in Chinese (`保存失败，请重试。` and friends), so replacing the
 * shared table would leave those pages with Chinese body text and an English denial —
 * and would break the assertions that pin the "never say retry for a 403" rule across
 * six test files.
 *
 * The KINDS stay single-sourced; only the wording is duplicated. Add a kind and the
 * Record type fails here until this table covers it too.
 */
export const DENIAL_HINTS_EN: Record<DenialKind, string> = {
  'write-not-permitted': `This operation modifies data and needs the ${IVT_EDITOR_GROUP} LDAP group; your account has read-only access. Ask the IVT Hub team to add you to ${IVT_EDITOR_GROUP}.`,
  'admin-not-permitted': `This operation is restricted to IVT Hub admins and needs the ${IVT_ADMIN_GROUP} LDAP group. Ask the IVT Hub team to add you to ${IVT_ADMIN_GROUP}.`,
  'route-unclassified':
    'The service has no authorization rule registered for this operation. That is a service configuration problem rather than a missing permission, so joining an LDAP group will not help. Please report it to the IVT Hub team.',
  'industry-not-permitted':
    'You do not have access to this industry. Ask the IVT Hub team for data access to it.',
  'not-authorized': `IVT Hub could not verify your identity: your account may not be in the ${IVT_VIEWER_GROUP} LDAP group, or your session expired. Refresh the page to sign in again; if it still fails, ask the IVT Hub team for IVT Hub access.`,
};

/**
 * User-facing message for an authorization denial, or `null` when the error is not
 * one. Call sites keep their own fallback:
 *
 *     setError(denialHint(e) ?? '创建失败');
 *
 * Passing a non-error (or `undefined`) yields `null`, so a `Promise.allSettled`
 * bucket can be classified without pre-filtering.
 *
 * `lang` defaults to Chinese so existing call sites are unchanged; the admin surface
 * passes `'en'` because its own copy is English.
 */
export function denialHint(e: unknown, lang: 'zh' | 'en' = 'zh'): string | null {
  const kind = denialKindOf(e);
  if (kind === null) return null;
  return lang === 'en' ? DENIAL_HINTS_EN[kind] : DENIAL_HINTS[kind];
}

/** True when the error is an authorization denial — i.e. retrying cannot help. */
export function isDenied(e: unknown): boolean {
  return denialKindOf(e) !== null;
}
